/*
 * Fix 5 — Mobile product image lightbox
 * Dependency-free. Opens a full-screen, swipeable, pinch-zoomable viewer
 * when a product gallery image is tapped on touch / small-screen devices.
 * Desktop keeps its existing hover-zoom / modal untouched.
 */
(function () {
  'use strict';

  // Only run where a touch lightbox makes sense (coarse pointer or small screen).
  function isMobileContext() {
    return (
      window.matchMedia('(pointer: coarse)').matches ||
      window.matchMedia('(max-width: 749px)').matches
    );
  }

  function buildLightbox() {
    var lb = document.createElement('div');
    lb.className = 'mobile-lightbox';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Product image viewer');
    lb.innerHTML =
      '<button type="button" class="mobile-lightbox__close" aria-label="Close image viewer">&times;</button>' +
      '<div class="mobile-lightbox__track"></div>' +
      '<div class="mobile-lightbox__counter" aria-hidden="true"></div>';
    document.body.appendChild(lb);
    return lb;
  }

  // Pick the best (largest) image URL we can from a gallery <img>.
  function bestImageUrl(img) {
    if (!img) return null;
    var url = img.currentSrc || img.src;
    // If a Shopify CDN url, request a large render for crisp zoom.
    if (url && /cdn\/shop|cdn\.shopify/.test(url)) {
      url = url.replace(/(\?|&)width=\d+/, '$1width=1946');
      if (!/[?&]width=/.test(url)) {
        url += (url.indexOf('?') === -1 ? '?' : '&') + 'width=1946';
      }
    }
    return url;
  }

  function init(gallery) {
    if (!gallery || gallery.dataset.mobileLightboxBound === 'true') return;
    gallery.dataset.mobileLightboxBound = 'true';

    var imgs = Array.prototype.slice.call(
      gallery.querySelectorAll('.product__media-item img')
    );
    if (!imgs.length) return;

    var lb, track, counter;
    var currentIndex = 0;
    var slideCount = 0;

    function ensureLightbox() {
      if (lb) return;
      lb = buildLightbox();
      track = lb.querySelector('.mobile-lightbox__track');
      counter = lb.querySelector('.mobile-lightbox__counter');

      lb.querySelector('.mobile-lightbox__close').addEventListener('click', close);
      lb.addEventListener('click', function (e) {
        // Tap on the dark backdrop (not the image) closes.
        if (e.target === lb || e.target === track) close();
      });
      bindSwipe();
      document.addEventListener('keydown', function (e) {
        if (!lb.classList.contains('is-open')) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowRight') goTo(currentIndex + 1);
        else if (e.key === 'ArrowLeft') goTo(currentIndex - 1);
      });
    }

    function renderSlides() {
      track.innerHTML = '';
      imgs.forEach(function (img) {
        var slide = document.createElement('div');
        slide.className = 'mobile-lightbox__slide';
        var big = document.createElement('img');
        big.className = 'mobile-lightbox__img';
        big.src = bestImageUrl(img);
        big.alt = img.alt || '';
        big.setAttribute('draggable', 'false');
        slide.appendChild(big);
        track.appendChild(slide);
      });
      slideCount = imgs.length;
    }

    function open(index) {
      ensureLightbox();
      renderSlides();
      currentIndex = index || 0;
      lb.classList.add('is-open');
      document.body.classList.add('mobile-lightbox-open');
      positionTrack(false);
      updateCounter();
    }

    function close() {
      if (!lb) return;
      lb.classList.remove('is-open');
      document.body.classList.remove('mobile-lightbox-open');
    }

    function updateCounter() {
      if (counter) counter.textContent = (currentIndex + 1) + ' / ' + slideCount;
    }

    function positionTrack(animate) {
      track.classList.toggle('is-dragging', !animate);
      track.style.transform = 'translateX(' + -currentIndex * 100 + '%)';
      if (!animate) {
        // force reflow then re-enable transition for subsequent moves
        void track.offsetWidth;
        track.classList.remove('is-dragging');
      }
    }

    function goTo(index) {
      currentIndex = Math.max(0, Math.min(slideCount - 1, index));
      positionTrack(true);
      updateCounter();
    }

    function bindSwipe() {
      var startX = 0;
      var startY = 0;
      var deltaX = 0;
      var deltaY = 0;
      var dragging = false;

      track.addEventListener(
        'touchstart',
        function (e) {
          // Ignore multi-touch (pinch-zoom) — let the browser handle it.
          if (e.touches.length !== 1) {
            dragging = false;
            return;
          }
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          deltaX = 0;
          deltaY = 0;
          dragging = true;
          track.classList.add('is-dragging');
        },
        { passive: true }
      );

      track.addEventListener(
        'touchmove',
        function (e) {
          if (!dragging || e.touches.length !== 1) return;
          deltaX = e.touches[0].clientX - startX;
          deltaY = e.touches[0].clientY - startY;
          var pct = (deltaX / window.innerWidth) * 100;
          track.style.transform =
            'translateX(' + (-currentIndex * 100 + pct) + '%)';
        },
        { passive: true }
      );

      track.addEventListener('touchend', function () {
        if (!dragging) return;
        dragging = false;
        var absX = Math.abs(deltaX);
        var absY = Math.abs(deltaY);

        // Swipe down to close (mostly vertical gesture).
        if (absY > 90 && absY > absX) {
          close();
          return;
        }
        // Horizontal swipe to change image.
        if (absX > window.innerWidth * 0.18) {
          goTo(currentIndex + (deltaX < 0 ? 1 : -1));
        } else {
          positionTrack(true);
        }
      });
    }

    // Attach open triggers to each gallery image.
    imgs.forEach(function (img, i) {
      var opener = img.closest('.product__media-item') || img;
      opener.addEventListener(
        'click',
        function (e) {
          if (!isMobileContext()) return; // desktop keeps native behaviour
          e.preventDefault();
          e.stopPropagation();
          open(i);
        },
        true
      );
    });
  }

  function boot() {
    document.querySelectorAll('media-gallery').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init if the gallery is replaced (e.g. variant change re-renders it).
  document.addEventListener('shopify:section:load', boot);
})();
